import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, X, TrendingDown } from 'lucide-react';
import { gaps } from '../../../data/comprehensiveFindingsData';
import './PantryRecruitmentGap.css';

const PantryRecruitmentGap: React.FC = () => {
  return (
    <div className="tmf-pantry-recruitment-gap">
      {/* Center visualization */}
      <div className="tmf-gap-visual-container">
        {/* Central pantry node */}
        <motion.div
          className="tmf-gap-center-node"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'backOut' }}
        >
          <div className="tmf-gap-center-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div className="tmf-gap-center-label">Pantry Visit</div>
        </motion.div>

        {/* Gap callout cards */}
        <div className="tmf-gap-cards-container">
          {gaps.map((gap, index) => {
            // Position gaps in a triangular layout around center
            const positions = [
              { top: '-10%', left: '35%', transform: 'translateX(-50%)' },
              { top: '50%', left: '10%', transform: 'translateY(-50%)' },
              { top: '50%', right: '10%', transform: 'translateY(-50%)' },
            ];

            return (
              <motion.div
                key={gap.id}
                className="tmf-gap-card"
                style={positions[index]}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.8 + index * 0.2,
                  ease: 'backOut',
                }}
              >
                {/* Pulsing icon */}
                <motion.div
                  className="tmf-gap-icon-wrapper"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.8, 1, 0.8],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3,
                  }}
                >
                  <AlertCircle className="tmf-gap-icon" size={24} />
                </motion.div>

                <div className="tmf-gap-number">Gap {gap.id}</div>
                <h5 className="tmf-gap-title">{gap.title}</h5>
                <p className="tmf-gap-description">{gap.description}</p>

                {/* Connecting line to center */}
                <motion.div
                  className="tmf-gap-connector"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 1 + index * 0.2 }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Summary insight */}
      <motion.div
        className="tmf-gap-summary"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 1.5 }}
      >
        <TrendingDown className="tmf-gap-summary-icon" size={20} />
        <p className="tmf-gap-summary-text">
          The pantry environment, while essential for food access, presents systemic challenges
          as a service recruitment touchpoint requiring alternative engagement strategies.
        </p>
      </motion.div>
    </div>
  );
};

export default PantryRecruitmentGap;
